/*
===============================================================================================================
                                 Copyright 2024-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React from 'react';
import PropTypes from 'prop-types';
import TextDiv from '@/components/text-div';
import InlineEditableText from '@/components/inline-editable-text';
import { getStaticCdnPath } from '@/utilities/static-build';

/**
 * @typedef {Object} CardConfig
 * @property {{ enabled: boolean, text: string }} label
 * @property {{ enabled: boolean, text: string }} title
 * @property {{ enabled: boolean, text: string }} description
 * @property {{ enabled: boolean, text: string, color: string }} footer
 */

/**
 * A return method card
 * @param {object} props
 * @param {{ src: string, alt: string }} props.icon - An icon config
 * @param {CardConfig} props.config - A card config
 * @param {boolean} props.editableMode - Whether or not the card has editable mode UI elements
 * @param {boolean} props.editEnabled - Whether or not the edit mode for the card is enabled
 * @param {boolean} props.disabled - Whether or not the card has the disabled look
 * @param {Function} props.onChange - A function called when a card field is updated
 * @return {JSX.Element}
 */
export default function ReturnMethodCard({
  icon,
  config: {
    label,
    title,
    description,
    footer,
    id,
  } = {},
  disabled,
  editableMode,
  editEnabled,
  onChange,
}) {
  const isPromoted = footer?.color;

  const renderTextElement = (elementConfig = {}, options = {}) => {
    if (editableMode) {
      return (
        <InlineEditableText
          inputSelectorId={`${id}-${elementConfig.id}-editable`}
          validationRules={{
            onSubmit: ['nonEmpty'],
            onChange: ['nonEmpty'],
          }}
          maxLength={options.maxLength}
          defaultValue={elementConfig.text || ''}
          defaultResetValue={elementConfig.defaultText}
          externalWrapperWidth={options.maxWidth}
          fontSize={options.fontSize}
          fontWeight={options.fontWeight}
          textAreaMode={options.textAreaMode}
          centered={options.centered}
          inputTextAlignment={options.textAlign}
          withTextOverflow={options.withTextOverflow}
          smallFontStyling
          shouldTranslateResetValue={false}
          readOnly={!editEnabled}
          onChange={(val) => {
            onChange(elementConfig.id, val);
          }}
        />
      );
    }
    return <TextDiv className="global-text-overflow">{elementConfig.text || elementConfig.defaultText}</TextDiv>;
  };

  return (
    <div className={`return-method-card${isPromoted ? ' promoted' : ''}${disabled ? ' disabled' : ''}${editableMode ? ' editable' : ''}`}>
      <div className="card-body">
        {label?.enabled && (
          <div className="card-promoted-tag">
            {renderTextElement(label, {
              fontSize: '10px',
              maxWidth: 170,
              fontWeight: 'var(--font-weight-footer)',
              maxLength: 25,
            })}
          </div>
        )}
        <div className="card-icon">
          <img src={`${getStaticCdnPath()}${icon?.src}`} alt={icon?.alt} />
        </div>
        <div className="card-description">
          {title?.enabled && (
            <div className="card-title"><TextDiv>{title.text}</TextDiv></div>
          )}
          {description?.enabled && (
            <div className="subtitle">
              {renderTextElement(description, {
                fontSize: '12px',
                maxWidth: 200,
                fontWeight: 'var(--font-weight-body)',
                textAreaMode: true,
                maxLength: 60,
              })}
            </div>
          )}
        </div>
      </div>
      {footer?.enabled && (
        <div className="card-footer">
          {renderTextElement(footer, {
            fontSize: '12px',
            maxWidth: 250,
            textAlign: 'center',
            centered: true,
            maxLength: 32,
            withTextOverflow: true,
          })}
        </div>
      )}
      <style jsx>
        {`
          .return-method-card {
            display: flex;
            flex-direction: column;
            font-family: var(--font-family-body-sans),Arial,Helvetica,sans-serif;
            justify-content: space-between;
            height: 100%;
            padding: 0 16px;
            border: 1px solid var(--color-card-border);
            border-radius: 3px;
            position: relative;
          }

          .card-title {
            font-size: var(--font-size-H2);
            font-family: var(--font-family-heading);
          }

          .return-method-card.disabled {
            filter: grayscale(1) opacity(0.55);
          }

          .card-body {
            display: flex;
            padding: 28px 0 10px;
          }

          .card-body:last-child {
            padding-bottom: 32px;
          }

          .card-promoted-tag {
            background-color: var(--color-warning-yellow);
            border-radius: 3px 0 12px 0;
            font-size: var(--font-size-H5);
            font-weight: 500;
            left: -1px;
            padding: 4px 12px 4px 8px;
            position: absolute;
            top: -1px;
            max-width: 150px;
            white-space: nowrap;
          }

          .return-method-card.editable .card-promoted-tag {
            max-width: none;
            padding: 0 8px 0 8px;
          }

          .card-icon {
            padding: 6px;
          }

          .card-description {
            display: flex;
            flex-direction: column;
            padding: 0 10px;
            row-gap: 3px;
          }

          .card-description .title {
            font-size: var(--font-size-H1);
            font-weight: var(--font-weight-H1);
          }

          .card-description .subtitle {
            font-size: var(--font-size-H4);
            line-height: 1.5;
            overflow-wrap: ${editEnabled ? 'normal' : 'anywhere'};;
            white-space: pre-wrap;
          }

          .return-method-card.editable .card-description {
            row-gap: 1px;
          }

          .card-footer {
            border-top: 1px solid var(--color-card-border);
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-H1);
            line-height: 1.5;
            padding: 16px 36px;
            text-align: center;
            white-space: nowrap;
            width: calc(100% + 32px);
            transform: translateX(-16px);
          }

          .return-method-card.editable .card-footer {
            padding:  ${editEnabled ? '13px 16px 13px 44px' : '13px'};
          }

          .return-method-card.promoted .card-footer {
            background-color: ${footer?.color || 'var(--color-white)'};
            border-color: transparent;
            transition: background-color 0.3s;
          }
        `}
      </style>
    </div>
  );
}

ReturnMethodCard.defaultProps = {
  icon: null,
  disabled: null,
  editableMode: false,
  editEnabled: false,
  onChange: null,
};

ReturnMethodCard.propTypes = {
  icon: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  config: PropTypes.shape({
    label: PropTypes.shape({
      enabled: PropTypes.bool,
      text: PropTypes.string,
    }),
    title: PropTypes.shape({
      enabled: PropTypes.bool,
      text: PropTypes.string,
    }),
    description: PropTypes.shape({
      enabled: PropTypes.bool,
      text: PropTypes.string,
    }),
    footer: PropTypes.shape({
      enabled: PropTypes.bool,
      text: PropTypes.string,
      color: PropTypes.string,
    }),
  }).isRequired,
  disabled: PropTypes.bool,
  editableMode: PropTypes.bool,
  editEnabled: PropTypes.bool,
  onChange: PropTypes.func,
};
